import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@/lib/apiClient";
import {
  fetchInfoScreenDeviceStatus,
  infoScreenDeviceStreamUrl,
  registerInfoScreenDevice,
} from "@/lib/infoScreenApi";
import type { InfoScreenDeviceStatus } from "@/types/infoScreen";

const STATUS_POLL_MS = 3_000;
const SSE_RECONNECT_MS = 5_000;

export function useInfoScreenDevice(displayCode: string) {
  const [deviceStatus, setDeviceStatus] = useState<InfoScreenDeviceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const bootstrapGenRef = useRef(0);
  const displayCodeRef = useRef(displayCode);
  displayCodeRef.current = displayCode;

  const refresh = useCallback(async (): Promise<InfoScreenDeviceStatus | null> => {
    const code = displayCodeRef.current;
    try {
      const status = await fetchInfoScreenDeviceStatus(code);
      if (displayCodeRef.current !== code) return null;
      setDeviceStatus(status);
      setError(null);
      return status;
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        try {
          const registered = await registerInfoScreenDevice(code);
          if (displayCodeRef.current !== code) return null;
          setDeviceStatus(registered);
          setError(null);
          return registered;
        } catch (registerErr) {
          if (displayCodeRef.current !== code) return null;
          const message =
            registerErr instanceof ApiError
              ? registerErr.message
              : "Gerät konnte nicht registriert werden.";
          setError(message);
          return null;
        }
      }
      if (displayCodeRef.current !== code) return null;
      const message =
        err instanceof ApiError ? err.message : "Gerätestatus konnte nicht geladen werden.";
      setError(message);
      return null;
    }
  }, []);

  const runBootstrap = useCallback(async () => {
    const gen = ++bootstrapGenRef.current;
    const codeAtStart = displayCodeRef.current;
    setLoading(true);
    setError(null);
    setDeviceStatus(null);
    try {
      await refresh();
    } finally {
      if (bootstrapGenRef.current === gen && displayCodeRef.current === codeAtStart) {
        setLoading(false);
      }
    }
  }, [refresh]);

  const register = useCallback(async () => {
    const code = displayCodeRef.current;
    setLoading(true);
    setError(null);
    try {
      const status = await refresh();
      if (displayCodeRef.current !== code) return;
      if (status) return;

      const registered = await registerInfoScreenDevice(code);
      if (displayCodeRef.current !== code) return;
      setDeviceStatus(registered);
      setError(null);
    } catch (err) {
      if (displayCodeRef.current !== code) return;
      const message =
        err instanceof ApiError ? err.message : "Gerät konnte nicht registriert werden.";
      setError(message);
    } finally {
      if (displayCodeRef.current === code) {
        setLoading(false);
      }
    }
  }, [refresh]);

  useEffect(() => {
    void runBootstrap();
  }, [displayCode, runBootstrap]);

  const isApproved = deviceStatus?.status === "approved";
  const awaitingApproval = !loading && !isApproved;

  useEffect(() => {
    if (!awaitingApproval) return;

    const timer = window.setInterval(() => {
      void refresh();
    }, STATUS_POLL_MS);
    return () => window.clearInterval(timer);
  }, [awaitingApproval, refresh]);

  useEffect(() => {
    if (!deviceStatus?.device_id || deviceStatus.status === "approved") {
      return;
    }

    let disposed = false;
    let eventSource: EventSource | null = null;
    let reconnectTimer: number | null = null;

    const clearReconnect = () => {
      if (reconnectTimer != null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const scheduleReconnect = () => {
      if (disposed || reconnectTimer != null) return;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, SSE_RECONNECT_MS);
    };

    const connect = () => {
      if (disposed) return;
      eventSource?.close();
      eventSource = null;

      try {
        eventSource = new EventSource(infoScreenDeviceStreamUrl(displayCodeRef.current));
      } catch {
        scheduleReconnect();
        return;
      }

      eventSource.addEventListener("info_screen_device_status", () => {
        void refresh();
      });

      eventSource.onerror = () => {
        eventSource?.close();
        eventSource = null;
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      disposed = true;
      eventSource?.close();
      clearReconnect();
    };
  }, [deviceStatus?.device_id, deviceStatus?.status, refresh]);

  return {
    deviceStatus,
    loading,
    error,
    isApproved,
    refresh,
    register,
  };
}
